import { BaseApiClient } from '../BaseApiClient'

export class ApiClient extends BaseApiClient {
  static getPdfBrowserUrl (application) {
    return this.getUrl(`pdf/browser?id=${application.id}&access-token=${this.getAccessToken()}`, true)
  }

  static getApplicationScreenUrl (search = null) {
    const path = search ? `profile?search=${search}` : 'profile'

    return this.getUrl(path, true)
  }

  static getApplicationArchiveUrl (search = null) {
    const path = search ? `profile?archived=1&search=${search}` : 'profile?archived=1'

    return this.getUrl(path, true)
  }

  static getVisitorScreenUrl () {
    return this.getUrl('visitors', true)
  }

  static getBulletinUrl () {
    return this.getUrl('pdf/app-bulletin', true)
  }

  static getUsersScreenUrl () {
    return this.getUrl('backend/user', true)
  }

  static async login (data) {
    return this.post('auth/login', data)
  }

  static async loginNoPassword (data) {
    return this.post('auth/login/no-password', data)
  }

  static async acceptLogin (data) {
    return this.post('auth/login/accept', data)
  }

  static async logout () {
    await this.backendLogout()

    return this.get('auth/logout', {
      headers: this.getAuthHeaders()
    })
  }

  static async userMe () {
    return this.get('user/me', {
      headers: this.getAuthHeaders()
    })
  }

  static async checkAccess () {
    return this.get('user/check-access', {
      headers: this.getAuthHeaders()
    })
  }

  static async checkCurrentVisitorApplications (data) {
    return this.post('application-services/check-current-visitor-applications', data, {
      headers: this.getAuthHeaders()
    })
  }

  static async checkBlacklist (personalId) {
    const data = {
      personal_id: personalId
    }

    return this.post('application-services/check-blacklist', data, {
      headers: this.getAuthHeaders()
    })
  }

  static async removeUploadedDocument (data) {
    return this.post('upload/remove', data, {
      headers: this.getAuthHeaders()
    })
  }

  static async getUploadDocuments (params) {
    return this.get('upload/get-docs', {
      params,
      headers: this.getAuthHeaders()
    })
  }

  static async getTransportDocuments (params) {
    return this.get('upload/get-transports-docs', {
      params,
      headers: this.getAuthHeaders()
    })
  }

  static async getApplicationDocuments (params) {
    return this.get('upload/get-application-docs', {
      params,
      headers: this.getAuthHeaders()
    })
  }

  static async createApplication (data) {
    return this.post('application-services/create-application', data, {
      headers: this.getAuthHeaders()
    })
  }

  static async editApplication (data) {
    return this.post('application-services/edit-application', data, {
      headers: this.getAuthHeaders()
    })
  }

  static async approveApplication (data) {
    return this.post('application-services/approve-application', data, {
      headers: this.getAuthHeaders()
    })
  }

  static async changeVisitorAccess (data) {
    return this.post('visitors/change-access', data, {
      headers: this.getAuthHeaders()
    })
  }

  static async importVisitorsFromFile (formData) {
    return this.post('application-services/import-visitors-from-file', formData, {
      headers: this.getAuthHeaders()
    })
  }

  static async applicationData (params) {
    return this.get('application-data', {
      params,
      headers: this.getAuthHeaders()
    })
  }

  static async vesselsData (params) {
    return this.get('vessels-data', {
      params,
      headers: this.getAuthHeaders()
    })
  }

  static async configurationData (params) {
    return this.get('configuration-data', {
      params,
      headers: this.getAuthHeaders()
    })
  }

  static async cfgMenuVisitorData (params) {
    return this.get('cfg-menu', {
      params,
      headers: this.getAuthHeaders()
    })
  }

  static async nationalityMaskData (params) {
    return this.get('nationality-document-mask-data', {
      params,
      headers: this.getAuthHeaders()
    })
  }

  static async transportPlateMaskData (params) {
    return this.get('transport-plate-mask-data', {
      params,
      headers: this.getAuthHeaders()
    })
  }

  static async extraApplicationData (params) {
    return this.get('application-data/extra-data', {
      params,
      headers: this.getAuthHeaders()
    })
  }

  static async changeFlagByVesselName (params) {
    return this.post('application-data/change-flag-by-vessel-name', params, {
      headers: this.getAuthHeaders()
    })
  }

  static async otherData (params) {
    return this.get('other-data', {
      params,
      headers: this.getAuthHeaders()
    })
  }

  static async fixApplicationActualDate (params) {
    return this.post('application/fix-actual-date', params, {
      headers: this.getAuthHeaders()
    })
  }

  static async issuePass (params) {
    return this.post('pass/give-pass', params, {
      headers: this.getAuthHeaders()
    })
  }

  static async mainHeaderData (params) {
    return this.get('other-data/main-header', {
      params,
      headers: this.getAuthHeaders()
    })
  }

  static async identificationPlaces (code) {
    return this.get('application-data/visitor-code', {
      params: {
        code
      },
      headers: this.getAuthHeaders()
    })
  }

  static async validateTransportNumber (number) {
    const data = { number }

    return this.post('application-services/validate-transport-number', data, {
      headers: this.getAuthHeaders()
    })
  }

  static async checkOrganizationInn (data) {
    return this.post('application-services/check-organization-inn', data, {
      headers: this.getAuthHeaders()
    })
  }

  static async sendSupport (data) {
    return this.post('user-services/support', data, {
      headers: this.getAuthHeaders()
    })
  }

  static async updateStatus (applicationId, data) {
    return this.post(`application-status/update?id=${applicationId}`, data, {
      headers: this.getAuthHeaders()
    })
  }

  static getTimeOfWork () {
    return this.get('application-data/time-of-work', {
      headers: this.getAuthHeaders()
    })
  }

  static getTerminalOrganization () {
    return this.get('organization-data/terminal-organization', {
      headers: ApiClient.getAuthHeaders()
    })
  }

  static getSignerStepOneUrl (application) {
    return this.getUrl(`application/step-one?id=${application.id}&access-token=${this.getAccessToken()}`)
  }

  static getSignerStepTwoUrl (application) {
    return this.getUrl(`application/step-two?id=${application.id}&access-token=${this.getAccessToken()}`)
  }

  static getGuardScreenUrl () {
    return this.getUrl('guard', true)
  }

  static getBlacklistScreenUrl () {
    return this.getUrl('black-list', true)
  }

  static getAccountUrl () {
    return this.getUrl('profile/account', true)
  }

  static getAdminPanel () {
    return this.getUrl('backend/user', true)
  }

  static uploadAvatar (data) {
    return this.post('upload/upload-avatar', data, {
      headers: this.getAuthHeaders()
    })
  }

  static logFrontendError (data) {
    return this.post('logger/index', data, {
      headers: this.getAuthHeaders()
    })
  }

  static sendToScud (data) {
    return this.post('xml/scud-xml', data, {
      headers: this.getAuthHeaders()
    })
  }

  static async sendVisitorToSolvo (data) {
    return this.post('xml/solvo-visitor-xml', data, {
      headers: this.getAuthHeaders()
    })
  }
}
